import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Starting OAuth callback handling...");
        
        // Process the OAuth callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Auth callback error:", error);
          navigate("/login", { replace: true });
          return;
        }

        if (!data.session) {
          console.error("No session found");
          navigate("/login", { replace: true });
          return;
        }

        console.log("Session data:", data.session);

        // Get the user data
        const { data: { user } } = await supabase.auth.getUser();
        console.log("Current user data:", user);
        
        if (user) {
          // Get existing metadata
          const existingMetadata = user.user_metadata || {};
          console.log("Existing metadata:", existingMetadata);
          
          // Get the raw user info from the OAuth provider
          const rawUserInfo = user.identities?.[0]?.identity_data;
          console.log("Raw user info from provider:", rawUserInfo);

          // Check if this is the first time logging in with this provider
          const currentProviders = existingMetadata.providers || [];
          const isFirstLogin = !currentProviders.includes(user.app_metadata.provider);

          // Prepare the metadata update
          const updatedMetadata = {
            ...existingMetadata,
            provider: user.app_metadata.provider,
            providers: Array.from(new Set([
              ...(existingMetadata.providers || []),
              user.app_metadata.provider
            ]))
          };

          // Only set the name if:
          // 1. This is the first login ever (no providers)
          // 2. OR the name is completely missing
          // 3. OR the name is an empty string
          if (currentProviders.length === 0 || !existingMetadata.full_name || existingMetadata.full_name.trim() === '') {
            updatedMetadata.full_name = rawUserInfo?.full_name ||
                                      rawUserInfo?.name ||
                                      user.user_metadata?.name ||
                                      user.user_metadata?.full_name;
          }

          console.log("Preparing to update with metadata:", updatedMetadata);

          // Update user metadata
          const { data: updateData, error: updateError } = await supabase.auth.updateUser({
            data: updatedMetadata
          });

          if (updateError) {
            console.error("Error updating user metadata:", updateError);
          } else {
            console.log("Successfully updated metadata:", updateData);
          }
        }

        // Redirect to home page
        navigate("/", { replace: true });
      } catch (error) {
        console.error("Unexpected error during auth callback:", error);
        navigate("/login", { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="h-screen flex flex-col items-center justify-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <p className="mt-4 text-lg">Completing authentication...</p>
    </div>
  );
};

export default AuthCallback;
